import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, Download, FileText, Sparkles, AlertTriangle, Info, ShieldAlert, HeartPulse } from "lucide-react";
import { ClinicalReviewPanel } from "@/components/ClinicalReviewPanel";

const DEFAULT_INPUTS = {
  losses_count: 2, primary_rpl: true, early_first_trimester_losses: false, late_losses: false,
  parental_balanced_translocation: false, paternal_robertsonian: false, prior_aneuploid_products: false, male_high_sdf: false,
  septate_uterus: false, submucous_fibroid_polyp: false, intrauterine_adhesions: false, cervical_incompetence: false, adenomyosis: false,
  thyroid_dysfunction: false, pcos_ir: false, hyperprolactinemia: false, poorly_controlled_dm: false, luteal_phase_defect: false,
  aps_positive: false, inherited_thrombophilia: false, th1_th2_imbalance: false, elevated_nk_ana: false, chronic_endometritis: false, hla_dsa: false,
  vit_d_deficiency: false, hyperhomocysteinemia: false, coeliac: false, obesity_bmi_high: false, smoking_alcohol: false,
};

const STEPS = [
  { key: "history", label: "Clinical history" },
  { key: "genetic", label: "Genetic" },
  { key: "anatomy", label: "Anatomical" },
  { key: "endocrine", label: "Endocrine" },
  { key: "immuno", label: "Immunology & thrombophilia" },
  { key: "emerging", label: "Emerging & systemic" },
  { key: "plan", label: "Generated plan" },
];

export default function RPLWorkflow() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [notes, setNotes] = useState("");
  const [referringDoctor, setReferringDoctor] = useState("");
  const [label, setLabel] = useState("");
  const [protocol, setProtocol] = useState(null);
  const [step, setStep] = useState(0);
  const [savedId, setSavedId] = useState(caseId || null);
  const [busy, setBusy] = useState(false);
  const [reviewData, setReviewData] = useState({ review: null, status: null, extended: null });

  useEffect(() => {
    if (!caseId) return;
    api.get(`/rpl/cases/${caseId}`).then(({ data }) => {
      setInputs({ ...DEFAULT_INPUTS, ...data.inputs });
      setLabel(data.patient_label);
      setNotes(data.notes || "");
      setReferringDoctor(data.referring_doctor || "");
      setProtocol(data.protocol);
      setSavedId(data.id);
      setReviewData({
        review: data.clinical_review || null,
        status: data.clinical_review_status || (data.clinical_review ? "ready" : null),
        extended: data.extended || null,
      });
      setStep(6);
    }).catch((e) => toast.error(formatApiError(e)));
    // Only re-fetch when the route param changes; setters and `api` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const patch = (k, v) => setInputs((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rpl/protocol/generate", inputs);
      setProtocol(data); setStep(6);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!label.trim()) { toast.error("Please add a patient label first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rpl/cases", { patient_label: label, notes, referring_doctor: referringDoctor || null, inputs });
      setSavedId(data.id);
      toast.success("RPL case saved");
      nav(`/rpl/${data.id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!savedId) { toast.error("Save the case first to export PDF"); return; }
    try {
      const res = await api.get(`/rpl/cases/${savedId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `RPL_plan_${label.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 reveal">
      <aside className="lg:col-span-3">
        <div className="eyebrow">RPL Workflow</div>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">Recurrent Pregnancy Loss</h1>
        <p className="mt-2 text-sm text-slate-600">ESHRE 2023 / ASRM 2020 evidence-graded work-up in 6 steps.</p>
        <ol className="mt-6 space-y-1">
          {STEPS.map((s, i) => (
            <li key={s.key}>
              <button
                onClick={() => setStep(i)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${step === i ? "bg-rose-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`rpl-step-tab-${i}`}>
                <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-bold ${step === i ? "bg-white text-rose-900" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                {s.label}
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <section className="lg:col-span-9 min-w-0">
        <div className="border border-slate-200 bg-white rounded-md p-6">
          {step === 0 && <History inputs={inputs} patch={patch} label={label} setLabel={setLabel} notes={notes} setNotes={setNotes} referringDoctor={referringDoctor} setReferringDoctor={setReferringDoctor} />}
          {step === 1 && <GeneticStep inputs={inputs} patch={patch} />}
          {step === 2 && <AnatomyStep inputs={inputs} patch={patch} />}
          {step === 3 && <EndocrineStep inputs={inputs} patch={patch} />}
          {step === 4 && <ImmunoStep inputs={inputs} patch={patch} />}
          {step === 5 && <EmergingStep inputs={inputs} patch={patch} />}
          {step === 6 && <PlanStep protocol={protocol} onGenerate={generate} busy={busy} />}

          <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-5">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="rpl-step-prev-btn">Back</Button>
            <div className="flex items-center gap-2">
              {step < 6 && (
                <Button
                  onClick={() => (step === 5 ? generate() : setStep((s) => s + 1))}
                  disabled={busy}
                  className="bg-rose-900 hover:bg-rose-800"
                  data-testid="rpl-step-next-btn">
                  {step === 5 ? (busy ? "Generating…" : "Generate protocol") : "Next"}
                </Button>
              )}
              {step === 6 && protocol && (
                <>
                  <Button variant="outline" onClick={save} disabled={busy} data-testid="rpl-save-case-btn">
                    <Save className="h-4 w-4 mr-2" strokeWidth={1.75} /> {savedId ? "Save as new case" : "Save case"}
                  </Button>
                  <Button className="bg-rose-900 hover:bg-rose-800" onClick={downloadPdf} disabled={!savedId} data-testid="rpl-download-pdf-btn">
                    <Download className="h-4 w-4 mr-2" strokeWidth={1.75} /> Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {step === 6 && protocol && savedId && (
          <ClinicalReviewPanel
            caseId={savedId}
            endpointBase="/rpl/cases"
            module="rpl"
            initialReview={reviewData.review}
            initialStatus={reviewData.status}
            initialExtended={reviewData.extended}
            onReviewReady={(rv) => setReviewData((s) => ({ ...s, review: rv, status: "ready" }))}
          />
        )}
      </section>
    </div>
  );
}

function StepHeader({ title, description }) {
  return (
    <div>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-600">{description}</p>
    </div>
  );
}
function CheckRow({ id, label, description, checked, onChange, testId }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 border border-slate-200 rounded-sm p-3 cursor-pointer hover:border-rose-900 transition-colors">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
      </div>
    </label>
  );
}

function History({ inputs, patch, label, setLabel, notes, setNotes, referringDoctor, setReferringDoctor }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 1 · Clinical history" description="Confirm the diagnostic criteria for RPL." />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="patient-label">Patient label</Label>
          <Input id="patient-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. S.P. — 32y" data-testid="rpl-input-patient-label" />
        </div>
        <div>
          <Label htmlFor="ref-doc">Referring doctor / clinic (optional)</Label>
          <Input id="ref-doc" maxLength={160} value={referringDoctor} onChange={(e) => setReferringDoctor(e.target.value)} data-testid="rpl-input-referring-doctor" />
        </div>
        <div>
          <Label htmlFor="lc">Number of documented losses</Label>
          <Input id="lc" type="number" min={0} max={15} value={inputs.losses_count} onChange={(e) => patch("losses_count", Number(e.target.value || 0))} data-testid="rpl-input-losses" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={inputs.primary_rpl} onCheckedChange={(v) => patch("primary_rpl", !!v)} data-testid="rpl-chk-primary" />
            Primary RPL (no prior live birth)
          </label>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="early" label="Mostly early first-trimester losses (< 10 w)" checked={inputs.early_first_trimester_losses} onChange={(v) => patch("early_first_trimester_losses", !!v)} testId="rpl-chk-early" />
        <CheckRow id="late" label="Late loss (≥ 10 w) or documented fetal loss" checked={inputs.late_losses} onChange={(v) => patch("late_losses", !!v)} testId="rpl-chk-late" />
      </div>
      <div>
        <Label htmlFor="notes">Clinical notes (optional)</Label>
        <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rpl-input-notes" />
      </div>
      <div className="border-l-4 border-slate-300 bg-slate-50 p-3 rounded-sm text-xs text-slate-600">
        <b>RPL definition (ESHRE 2023):</b> two or more clinical (or biochemical) pregnancy losses, not necessarily consecutive. ASRM 2020 requires two or more clinical pregnancy losses.
      </div>
    </div>
  );
}

function GeneticStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 2 · Genetic" description="Parental karyotype, embryonic ploidy and sperm DNA integrity." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="bt" label="Parental balanced translocation" checked={inputs.parental_balanced_translocation} onChange={(v) => patch("parental_balanced_translocation", !!v)} testId="rpl-chk-bt" />
        <CheckRow id="rob" label="Robertsonian translocation" checked={inputs.paternal_robertsonian} onChange={(v) => patch("paternal_robertsonian", !!v)} testId="rpl-chk-rob" />
        <CheckRow id="paneu" label="Prior aneuploid products of conception" checked={inputs.prior_aneuploid_products} onChange={(v) => patch("prior_aneuploid_products", !!v)} testId="rpl-chk-paneu" />
        <CheckRow id="sdf" label="High sperm DNA fragmentation (SDF > 30%)" checked={inputs.male_high_sdf} onChange={(v) => patch("male_high_sdf", !!v)} testId="rpl-chk-sdf" />
      </div>
    </div>
  );
}

function AnatomyStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 3 · Anatomical / uterine" description="Congenital uterine anomalies, acquired lesions and cervical competence." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="sep" label="Septate uterus" checked={inputs.septate_uterus} onChange={(v) => patch("septate_uterus", !!v)} testId="rpl-chk-septum" />
        <CheckRow id="sm" label="Submucous fibroid / polyp" checked={inputs.submucous_fibroid_polyp} onChange={(v) => patch("submucous_fibroid_polyp", !!v)} testId="rpl-chk-fibroid" />
        <CheckRow id="ash" label="Intrauterine adhesions (Asherman)" checked={inputs.intrauterine_adhesions} onChange={(v) => patch("intrauterine_adhesions", !!v)} testId="rpl-chk-asherman" />
        <CheckRow id="ci" label="Cervical incompetence / mid-trimester loss" checked={inputs.cervical_incompetence} onChange={(v) => patch("cervical_incompetence", !!v)} testId="rpl-chk-cerclage" />
        <CheckRow id="ad" label="Adenomyosis" checked={inputs.adenomyosis} onChange={(v) => patch("adenomyosis", !!v)} testId="rpl-chk-adeno" />
      </div>
    </div>
  );
}

function EndocrineStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 4 · Endocrine" description="Thyroid, insulin resistance, prolactin, glycaemic control and luteal support." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="th" label="Thyroid dysfunction / autoimmunity" description="TSH > 2.5 or anti-TPO+" checked={inputs.thyroid_dysfunction} onChange={(v) => patch("thyroid_dysfunction", !!v)} testId="rpl-chk-thyroid" />
        <CheckRow id="pcos" label="PCOS / insulin resistance" checked={inputs.pcos_ir} onChange={(v) => patch("pcos_ir", !!v)} testId="rpl-chk-pcos" />
        <CheckRow id="prl" label="Hyperprolactinemia" checked={inputs.hyperprolactinemia} onChange={(v) => patch("hyperprolactinemia", !!v)} testId="rpl-chk-prl" />
        <CheckRow id="dm" label="Poorly controlled diabetes" description="HbA1c ≥ 6.5" checked={inputs.poorly_controlled_dm} onChange={(v) => patch("poorly_controlled_dm", !!v)} testId="rpl-chk-dm" />
        <CheckRow id="lpd" label="Luteal phase defect / low progesterone" checked={inputs.luteal_phase_defect} onChange={(v) => patch("luteal_phase_defect", !!v)} testId="rpl-chk-lpd" />
      </div>
    </div>
  );
}

function ImmunoStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 5 · Immunology & thrombophilia" description="APS, inherited thrombophilia, Th1/Th2, NK cells, chronic endometritis." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="aps" label="Antiphospholipid syndrome" description="LA / aCL / anti-β2GP1 twice, 12 w apart" checked={inputs.aps_positive} onChange={(v) => patch("aps_positive", !!v)} testId="rpl-chk-aps" />
        <CheckRow id="thromb" label="Inherited thrombophilia" description="FVL / prothrombin / PC / PS / AT" checked={inputs.inherited_thrombophilia} onChange={(v) => patch("inherited_thrombophilia", !!v)} testId="rpl-chk-thromb" />
        <CheckRow id="th12" label="Th1/Th2 imbalance" checked={inputs.th1_th2_imbalance} onChange={(v) => patch("th1_th2_imbalance", !!v)} testId="rpl-chk-th12" />
        <CheckRow id="nkana" label="Elevated NK cells / ANA" checked={inputs.elevated_nk_ana} onChange={(v) => patch("elevated_nk_ana", !!v)} testId="rpl-chk-nk" />
        <CheckRow id="ce" label="Chronic endometritis (CD138+)" checked={inputs.chronic_endometritis} onChange={(v) => patch("chronic_endometritis", !!v)} testId="rpl-chk-ce" />
        <CheckRow id="dsa" label="Anti-paternal HLA / DSA" checked={inputs.hla_dsa} onChange={(v) => patch("hla_dsa", !!v)} testId="rpl-chk-dsa" />
      </div>
    </div>
  );
}

function EmergingStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 6 · Emerging & systemic" description="Vitamin D, homocysteine, coeliac, lifestyle." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="vitd" label="Vitamin D deficiency" description="25-OH-D < 20 ng/mL" checked={inputs.vit_d_deficiency} onChange={(v) => patch("vit_d_deficiency", !!v)} testId="rpl-chk-vitd" />
        <CheckRow id="hcy" label="Hyperhomocysteinemia (couple)" checked={inputs.hyperhomocysteinemia} onChange={(v) => patch("hyperhomocysteinemia", !!v)} testId="rpl-chk-hcy" />
        <CheckRow id="cel" label="Coeliac disease" checked={inputs.coeliac} onChange={(v) => patch("coeliac", !!v)} testId="rpl-chk-coeliac" />
        <CheckRow id="bmi" label="Obesity (BMI ≥ 30)" checked={inputs.obesity_bmi_high} onChange={(v) => patch("obesity_bmi_high", !!v)} testId="rpl-chk-bmi" />
        <CheckRow id="life" label="Smoking / alcohol / recreational drug use" checked={inputs.smoking_alcohol} onChange={(v) => patch("smoking_alcohol", !!v)} testId="rpl-chk-lifestyle" />
      </div>
    </div>
  );
}

function severityStyle(sev) {
  if (sev === "critical") return { cls: "severity-critical", icon: ShieldAlert, color: "text-red-700" };
  if (sev === "warning") return { cls: "severity-warning", icon: AlertTriangle, color: "text-amber-700" };
  return { cls: "severity-info", icon: Info, color: "text-teal-700" };
}

function PlanStep({ protocol, onGenerate, busy }) {
  if (!protocol) {
    return (
      <div className="text-center py-10">
        <Sparkles className="h-8 w-8 mx-auto text-rose-900" strokeWidth={1.5} />
        <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">Generate the RPL protocol</h2>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">Compose a dosed, evidence-graded management plan for this couple.</p>
        <Button onClick={onGenerate} disabled={busy} className="mt-6 h-11 px-6 bg-rose-900 hover:bg-rose-800" data-testid="rpl-generate-protocol-btn">
          {busy ? "Generating…" : "Generate protocol"}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="rpl-generated-plan">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-rose-900" strokeWidth={1.75} /> RPL management plan
        </h2>
        <p className="mt-1 text-sm text-slate-600">{protocol.diagnosis_summary}</p>
      </div>
      {protocol.sections.map((sec) => (
        <div key={sec.key} className="space-y-3">
          <div className="eyebrow">{sec.title}</div>
          {sec.items.length === 0 && <div className="text-sm text-slate-500 italic">No targeted findings in this domain.</div>}
          {sec.items.map((it) => {
            const s = severityStyle(it.severity);
            const Icon = s.icon;
            return (
              <div key={it.key} className={`border-l-4 rounded-sm p-4 ${s.cls}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${s.color}`} strokeWidth={1.75} />
                  <div className="font-semibold text-slate-900">{it.title}</div>
                </div>
                <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-6">
                  {it.lines.map((l, i) => <li key={`${it.key}-line-${i}`}>{l}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
